import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { buildApiUrl } from '../../config/api';
import { formatDrfSerializerErrors } from '../../utils/apiResponseHandler';
import './EditApplicationForm.css';

const EditApplicationForm = () => {
  const { token } = useParams();
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [editLinkInfo, setEditLinkInfo] = useState(null);

  useEffect(() => {
    fetchApplicationData();
  }, [token]);

  const fetchApplicationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use absolute API URL instead of relative path
      const apiUrl = buildApiUrl(`/api/members/edit-application/${token}/`);
      const response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      // Check content type to ensure we got JSON, not HTML
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Received non-JSON response:', text.substring(0, 200));
        throw new Error('Server returned invalid response format. Please check if the backend is running.');
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || errorData.error || 'Failed to fetch application data');
      }
      
      const data = await response.json();
      setFormData(data.member);
      setEditLinkInfo(data.edit_link_info);
    } catch (err) {
      console.error('Error fetching application data:', err);
      setError(err.message || 'Failed to load application data. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Use absolute API URL instead of relative path
      const apiUrl = buildApiUrl(`/api/members/edit-application/${token}/`);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Check content type to ensure we got JSON, not HTML
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Received non-JSON response:', text.substring(0, 200));
        throw new Error('Server returned invalid response format. Please try again.');
      }

      if (!response.ok) {
        const errorData = await response.json();
        const drfMsg = formatDrfSerializerErrors(errorData);
        throw new Error(
          errorData.message ||
            errorData.error ||
            drfMsg ||
            'Failed to update application'
        );
      }

      const result = await response.json();
      setSuccess(true);
      setTimeout(() => {
        // Try to close window, fallback to showing message if blocked
        if (!window.close()) {
          // If close fails, the success message already shows instructions
        }
      }, 3000);
    } catch (err) {
      console.error('Error submitting application:', err);
      setError(err.message || 'Failed to update application. Please try again or contact support.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-application-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading application data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-application-container">
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Try Again</button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="edit-application-container">
        <div className="success-message">
          <h2>✅ Application Updated Successfully!</h2>
          <p>Your changes have been saved and the application has been updated.</p>
          <p>You can now close this window or tab.</p>
            <button 
              onClick={() => window.close()}
              className="close-button"
            >
              Close Window
            </button>
          </div>
        </div>
    );
  }

  return (
    <div className="edit-application-container">
      <div className="edit-header">
        <h1>Edit Membership Application</h1>
        <p>Organization: <strong>{formData.organization_name}</strong></p>
        {editLinkInfo && (
          <div className="link-info">
            <p>Sent to: {editLinkInfo.recipient_email} ({editLinkInfo.recipient_role})</p>
            <p>Expires: {new Date(editLinkInfo.expires_at).toLocaleString()}</p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="edit-form">
        <div className="form-section">
          <h3>Organization Information</h3>
          <div className="form-group">
            <label htmlFor="organization_name">Organization Name</label>
            <input
              type="text"
              id="organization_name"
              name="organization_name"
              value={formData.organization_name || ''}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Address Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="street_address_1">Street Address</label>
              <input
                type="text"
                id="street_address_1"
                name="street_address_1"
                value={formData.street_address_1 || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="city">City</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city || ''}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="state">State</label>
              <select
                id="state"
                name="state"
                value={formData.state || ''}
                onChange={handleInputChange}
                required
              >
                <option value="">Select State</option>
                <option value="NSW">New South Wales</option>
                <option value="VIC">Victoria</option>
                <option value="QLD">Queensland</option>
                <option value="WA">Western Australia</option>
                <option value="SA">South Australia</option>
                <option value="TAS">Tasmania</option>
                <option value="ACT">Australian Capital Territory</option>
                <option value="NT">Northern Territory</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="postcode">Postcode</label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                value={formData.postcode || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>President Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="president_name">President Name</label>
              <input
                type="text"
                id="president_name"
                name="president_name"
                value={formData.president_name || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="president_email">President Email</label>
              <input
                type="email"
                id="president_email"
                name="president_email"
                value={formData.president_email || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="president_phone">President Phone</label>
            <input
              type="tel"
              id="president_phone"
              name="president_phone"
              value={formData.president_phone || ''}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Secretary Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="secretary_name">Secretary Name</label>
              <input
                type="text"
                id="secretary_name"
                name="secretary_name"
                value={formData.secretary_name || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="secretary_email">Secretary Email</label>
              <input
                type="email"
                id="secretary_email"
                name="secretary_email"
                value={formData.secretary_email || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="secretary_phone">Secretary Phone</label>
            <input
              type="tel"
              id="secretary_phone"
              name="secretary_phone"
              value={formData.secretary_phone || ''}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Treasurer Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="treasurer_name">Treasurer Name</label>
              <input
                type="text"
                id="treasurer_name"
                name="treasurer_name"
                value={formData.treasurer_name || ''}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label htmlFor="treasurer_email">Treasurer Email</label>
              <input
                type="email"
                id="treasurer_email"
                name="treasurer_email"
                value={formData.treasurer_email || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="treasurer_phone">Treasurer Phone</label>
            <input
              type="tel"
              id="treasurer_phone"
              name="treasurer_phone"
              value={formData.treasurer_phone || ''}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Screening Information</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="screening_frequency">Screening Frequency</label>
              <select
                id="screening_frequency"
                name="screening_frequency"
                value={formData.screening_frequency || ''}
                onChange={handleInputChange}
              >
                <option value="">Select Frequency</option>
                <option value="weekly">Weekly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annually">Annually</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="screening_format">Screening Format</label>
              <select
                id="screening_format"
                name="screening_format"
                value={formData.screening_format || ''}
                onChange={handleInputChange}
              >
                <option value="">Select Format</option>
                <option value="16mm">16mm</option>
                <option value="dvd">DVD</option>
                <option value="blu_ray">Blu-ray</option>
                <option value="dci">DCI</option>
                <option value="4k">4K</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="screening_venue">Screening Venue</label>
            <input
              type="text"
              id="screening_venue"
              name="screening_venue"
              value={formData.screening_venue || ''}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={saving} className="save-button">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditApplicationForm;
